import React from "react";
import styled from "styled-components";
import { FiCheck } from "react-icons/fi";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0;
  @media (max-width: 960px) {
    padding: 40px 0;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1150px;
  gap: 20px;
  padding: 0 20px;
`;

const Tag = styled.div`
  background: rgba(133, 76, 230, 0.12);
  border: 1px solid rgba(133, 76, 230, 0.4);
  color: #c084fc;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 18px;
  border-radius: 9999px;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 32px;
  width: 100%;
  margin-top: 15px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h2`
  font-size: 38px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.25;
  letter-spacing: -0.02em;

  @media (max-width: 960px) {
    font-size: 30px;
    text-align: center;
  }
  @media (max-width: 640px) {
    font-size: 26px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);

  &:hover {
    border-color: rgba(133, 76, 230, 0.5);
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(133, 76, 230, 0.15);
  }
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
  margin-bottom: 6px;
  background: linear-gradient(135deg, #ffffff 40%, rgba(255, 255, 255, 0.7) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 640px) {
    font-size: 26px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  line-height: 1.4;
`;

const RightCard = styled.div`
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(133, 76, 230, 0.35);
  }

  @media (max-width: 640px) {
    padding: 24px 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;

  strong {
    color: #ffffff;
    font-weight: 600;
  }
`;

const FocusHeading = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  margin-top: 10px;
  margin-bottom: 2px;
`;

const FocusGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FocusItem = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(133, 76, 230, 0.1);
    border-color: rgba(133, 76, 230, 0.3);
    color: #ffffff;
  }

  svg {
    color: #a855f7;
    font-size: 16px;
    flex-shrink: 0;
  }
`;

const About = () => {
  const stats = [
    { number: "3+", label: "Industry Roles" },
    { number: "3+", label: "Featured Projects" },
    { number: "MERN", label: "Core Tech Stack" },
    { number: "100%", label: "Dedication to Quality" },
  ];

  const focusAreas = [
    "Full Stack Development",
    "React & Next.js",
    "Node.js & Express",
    "MongoDB & MySQL",
    "REST APIs",
    "Responsive UI",
    "Clean Architecture",
    "Performance Optimization",
  ];

  return (
    <Container id="about">
      <Wrapper>
        <Tag>About Me</Tag>

        <ContentGrid>
          {/* Left Column */}
          <LeftSection>
            <Title>
              Passionate engineer creating scalable, impactful digital solutions.
            </Title>

            <StatsGrid>
              {stats.map((stat, idx) => (
                <StatCard key={idx}>
                  <StatNumber>{stat.number}</StatNumber>
                  <StatLabel>{stat.label}</StatLabel>
                </StatCard>
              ))}
            </StatsGrid>
          </LeftSection>

          {/* Right Column */}
          <RightCard>
            <Paragraph>
              I am a <strong>Full Stack Developer</strong> focused on building
              robust, high-performance web applications. My development
              philosophy centers around clean code, modular architectures, and
              creating seamless user experiences that scale effortlessly.
            </Paragraph>

            <Paragraph>
              With strong expertise in both frontend engineering{" "}
              <strong>(React, Next.js, Tailwind CSS)</strong> and backend
              systems{" "}
              <strong>(Node.js, Express, MongoDB, MySQL, REST APIs)</strong>, I
              handle the entire lifecycle of modern web projects from concept to
              deployment.
            </Paragraph>

            <FocusHeading>Core Focus Areas:</FocusHeading>

            <FocusGrid>
              {focusAreas.map((area, idx) => (
                <FocusItem key={idx}>
                  <FiCheck />
                  <span>{area}</span>
                </FocusItem>
              ))}
            </FocusGrid>
          </RightCard>
        </ContentGrid>
      </Wrapper>
    </Container>
  );
};

export default About;